import React, { useEffect, useState } from "react";
import CustomSelect from "@/components/Select/Select";
import { calculateAge } from "@/helpers/calculateAge";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  SelectChangeEvent,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useGetUsersQuery,
  useUpdateUserMutation,
} from "@/store/rtk-api/users.api";
import { IUser, Profile, UserRole } from "@/store/models/users.models";
import styles from "./UserPage.module.scss";
import { IAdminFireBase } from "@/store/models/admin.models";
import { withAdmin } from "@/services/withAdmin";
import Layout from "@/components/Layout/Layout";
import { AddChildDialog } from "./components/AddChildDialog";
import { StyledInput } from "@/components/Input/Input";
import styled from "styled-components";

export const StyledLabel = styled.label`
  width: 100%;
  display: block;
  padding: 10px;
  font-size: 16px;
  border: 1px solid #cfd3d4;
  border-radius: 8px;
  color: #5e6366;
  font-family: "Mont", sans-serif;
  text-align: left;
`;

const options = Object.values(UserRole)
  .slice(0, 3)
  .map((role) => ({
    value: role,
    label: role.charAt(0).toUpperCase() + role.slice(1),
  }));

const UserPageCmp: React.FC<{ admin: IAdminFireBase }> = ({ admin }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const user: IUser = location.state?.user;
  const [selectedRole, setSelectedRole] = useState(
    user?.role || UserRole.free || UserRole.premiumUnlimited || UserRole.premium
  );
  const [newTokensAmount, setNewTokensAmount] = useState<number>(0);
  const [prevTokensAmount, setPrevTokensAmount] = useState<number>(0);
  const [isBanned, setIsBanned] = useState(user?.role === UserRole.banned);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTokensOpen, setDialogTokensOpen] = useState(false);
  const [newRole, setNewRole] = useState<UserRole>(selectedRole);
  const [prevRole, setPrevRole] = useState<UserRole>(selectedRole);
  const [addChildDialogOpen, setAddChildDialogOpen] = useState(false);

  const {
    data: userData,
    isSuccess,
    refetch,
  } = useGetUsersQuery({ uid: user.uid[0] });

  useEffect(() => {
    if (userData && isSuccess && userData.data[0].subscription) {
      setNewTokensAmount(userData?.data[0].subscription.expirablePoints);
      setPrevTokensAmount(userData?.data[0].subscription.expirablePoints);
    }
  }, [userData, isSuccess]);

  const [updateUser] = useUpdateUserMutation();
  const handleRoleChange = (event: SelectChangeEvent<UserRole>) => {
    const role = event.target.value as UserRole;
    setPrevRole(selectedRole);
    setNewRole(role);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleDialogTokensConfirm = async () => {
    setDialogTokensOpen(false);

    await updateUser({
      uid: userData!.data[0].internal_id,
      expirablePoints: newTokensAmount,
    });
  };

  const handleDialogConfirm = async () => {
    setDialogOpen(false);
    setSelectedRole(newRole);
    setIsBanned(newRole === UserRole.banned);

    await updateUser({ uid: userData!.data[0].internal_id, role: newRole });
  };

  const handleNavigateToProfile = (profile: Profile) => {
    navigate(`/users/${user.email}/${profile.name}`, {
      state: { profile, user },
    });
  };

  return (
    <Layout admin={admin}>
      <div className={styles.container}>
        <div className={styles.userInfo}>
          <div className={styles.userInfoTop}>
            <p className={styles.userEmail}>{user?.email}</p>
          </div>
          <div className={styles.settings}>
            <CustomSelect
              label="Role"
              options={options}
              value={selectedRole}
              onChange={handleRoleChange}
              disabled={isBanned}
            />
          </div>

          <div className={styles.tokens}>
            <StyledLabel
              htmlFor="example-input"
              onClick={() => setDialogTokensOpen(true)}
            >
              {prevTokensAmount}
            </StyledLabel>
          </div>
        </div>
        <div className={styles.rightPart}>
          <div className={styles.characterCards}>
            {isSuccess &&
              userData.data[0].profiles.map((profile) => (
                <div
                  className={styles.characterCard}
                  onClick={() => handleNavigateToProfile(profile)}
                >
                  <div className={styles.characterLeft}>
                    <div className={styles.characterInfo}>
                      <p className={styles.characterName}>{profile.name}</p>
                      <p className={styles.characterYears}>
                        {`${calculateAge(profile.birthday)} years old`}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
          </div>

          <Button
            variant="outlined"
            onClick={() => setAddChildDialogOpen(true)}
            style={{
              width: "150px",
              textTransform: "capitalize",
              display: "none",
            }}
          >
            Add Child
          </Button>
        </div>
      </div>
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Change user role?</DialogTitle>
        <DialogContent>
          Are you sure you want to change the user's role from {prevRole} to{" "}
          {newRole}? It will affect the user experience and change access to
          certain features.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleDialogConfirm} color="primary">
            Change
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogTokensOpen}
        onClose={() => setDialogTokensOpen(false)}
      >
        <DialogTitle>Change user tokens amount?</DialogTitle>
        <DialogContent>
          Are you sure you want to change the user's amount of token from{" "}
          {prevTokensAmount} to{" "}
          <StyledInput
            id="input-text"
            type="number"
            value={Number(newTokensAmount)}
            onChange={(e: any) => {
              setNewTokensAmount(e.target.value.toString());
            }}
          />
          ? It will affect the user experience.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogTokensOpen(false)}>Cancel</Button>
          <Button onClick={handleDialogTokensConfirm} color="primary">
            Change
          </Button>
        </DialogActions>
      </Dialog>

      {addChildDialogOpen && userData && (
        <AddChildDialog
          addChildDialogOpen={addChildDialogOpen}
          setAddChildDialogOpen={setAddChildDialogOpen}
          user={user}
          refetch={refetch}
        />
      )}
    </Layout>
  );
};

export const UserPage = withAdmin(UserPageCmp);
UserPageCmp.displayName = "UserPageCmp";
